import { useNavigate } from "react-router-dom";
import { api, errText, fmt } from "@/lib/api";
import { shortDate } from "@/lib/partners";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FilePlus2, ArrowUpRight } from "lucide-react";

export const RealtorLeads = ({ realtor, onChanged }) => {
  const navigate = useNavigate();

  const convert = async (leadId) => {
    try {
      const { data } = await api.post(`/leads/${leadId}/convert`);
      toast.success("Quote created — finish pricing and send it");
      onChanged?.();
      navigate(`/admin/quotes/${data.quote_id}`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="space-y-10" data-testid="realtor-leads">
      <div>
        <h3 className="font-display text-xl mb-4">Private client leads</h3>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Moving</th>
                <th className="px-4 py-3 font-medium">Pieces</th>
                <th className="px-4 py-3 font-medium">Received</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {(realtor.leads || []).length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No client leads yet.</td></tr>
              )}
              {(realtor.leads || []).map((l) => (
                <tr key={l.id} className="border-b border-gray-100" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3.5 font-semibold whitespace-nowrap">{l.customer.first_name} {l.customer.last_name}</td>
                  <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">{l.customer.email}<br />{l.customer.phone || "—"}</td>
                  <td className="px-4 py-3.5">{l.moving ? `Yes${l.move_date ? ` · ${l.move_date}` : ""}` : "No"}</td>
                  <td className="px-4 py-3.5 max-w-[240px] text-gray-600">{l.pieces || "—"}</td>
                  <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{shortDate(l.created_at)}</td>
                  <td className="px-4 py-3.5">
                    <span className={`text-[11px] px-2 py-1 font-semibold tracking-wide ${l.status === "Quoted" ? "bg-[#c19a6b]/15 text-[#8a6a42]" : "bg-blue-50 text-blue-700"}`}>{l.status}</span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    {l.quote_id ? (
                      <Button data-testid={`open-lead-quote-${l.id}`} onClick={() => navigate(`/admin/quotes/${l.quote_id}`)} variant="outline" size="sm" className="h-9 rounded-sm text-[11px] uppercase tracking-wider">
                        Open Quote <ArrowUpRight size={12} className="ml-1" />
                      </Button>
                    ) : (
                      <Button data-testid={`convert-lead-${l.id}`} onClick={() => convert(l.id)} size="sm" className="h-9 bg-[#c19a6b] hover:bg-[#b08a5c] text-white rounded-sm text-[11px] uppercase tracking-wider btn-lift">
                        <FilePlus2 size={12} className="mr-1.5" /> Create Quote
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl mb-4">Attributed quotes</h3>
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-[0.12em] text-gray-500">
                <th className="px-4 py-3 font-medium">Quote #</th>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Invoiced</th>
                <th className="px-4 py-3 font-medium text-right">Client Savings</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {(realtor.quotes || []).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">No attributed quotes yet.</td></tr>
              )}
              {(realtor.quotes || []).map((q) => (
                <tr
                  key={q.id}
                  data-testid={`attributed-quote-${q.quote_number}`}
                  onClick={() => navigate(`/admin/quotes/${q.id}`)}
                  className="border-b border-gray-100 hover:bg-[#FAF7F2] cursor-pointer transition-colors duration-150"
                >
                  <td className="px-4 py-3.5 font-semibold whitespace-nowrap">{q.quote_number}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">{q.customer_name}</td>
                  <td className="px-4 py-3.5">{q.status}</td>
                  <td className="px-4 py-3.5 text-right">{q.amount_cents ? fmt(q.amount_cents) : "—"}</td>
                  <td className="px-4 py-3.5 text-right text-[#8a6a42]">{q.savings_cents ? fmt(q.savings_cents) : "—"}</td>
                  <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{shortDate(q.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
